import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Clock, Calendar, Coffee, Palette, Book, Utensils, TreePine, Heart } from 'lucide-react';
import SEO from '../components/SEO';

const Dagopvang = () => {
  const activities = [
    { icon: Palette, title: 'Creatief Werk', description: 'Knutselen, tekenen, schilderen en andere creatieve activiteiten' },
    { icon: Utensils, title: 'Koken & Bakken', description: 'Samen gezonde maaltijden bereiden en leren over voeding' },
    { icon: Book, title: 'Educatieve Spellen', description: 'Spelenderwijs leren en ontwikkelen van vaardigheden' },
    { icon: TreePine, title: 'Buitenactiviteiten', description: 'Wandelen, fietsen en spelen in de natuur' },
    { icon: Coffee, title: 'Prikkelarme Momenten', description: 'Rustige activiteiten en ontspanning voor herstel' },
    { icon: Heart, title: 'Sociale Vaardigheden', description: 'Oefenen met samenwerken en communicatie' }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Dagopvang Jeugd Nieuwstadt"
        description="Dagopvang voor jeugd 3-18 jaar met ASS, ADHD of beperking. Open 08:00-20:00. Financiering via WLZ, WMO en Jeugdwet. Bel 06-19199023."
        keywords="dagopvang, dagbesteding, jeugdzorg, activiteiten, structuur, Nieuwstadt"
        
      />
      <Header />
      
      {/* Hero */}
      <section 
        className="pt-32 pb-16 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_skole-clone/artifacts/dhab419f_IMG_8162.jpeg)',
          minHeight: '500px'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <h1 
              className="text-5xl md:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}
            >
              Dagopvang
            </h1>
            <p className="text-xl text-white leading-relaxed mb-8">
              Structuur, dagelijkse vaardigheden, sociale ontwikkeling en plezier in een veilige, huiselijke omgeving.
            </p>
            <a href="mailto:info@kscare.nl?subject=Aanmelding%20dagopvang">
              <Button className="bg-[#FF6347] hover:bg-[#FF5533] text-white rounded-full px-8 py-6 text-lg">
                Aanmelden voor dagopvang
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-[#1A4D4D] mb-6 text-center" style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}>
              Wat bieden wij?
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed text-center">
              Onze dagbesteding richt zich op structuur, het ontwikkelen van dagelijkse vaardigheden, sociale ontwikkeling en vooral: plezier! Elke dag biedt een duidelijk programma met afwisseling tussen actieve en passieve momenten.
            </p>
          </div>

          {/* Activities Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {activities.map((activity, index) => {
              const Icon = activity.icon;
              return (
                <Card key={index} className="p-6 rounded-3xl border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#FFE8E0] to-[#FFD0C0] rounded-full flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-[#FF6347]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A4D4D] mb-2">{activity.title}</h3>
                  <p className="text-gray-600">{activity.description}</p>
                </Card>
              );
            })}
          </div>

          {/* Program Note */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-12 text-center">
            <h3 className="text-3xl font-bold text-[#1A4D4D] mb-6" style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}>
              Programma op maat
            </h3>
            <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto">
              Het dagprogramma is volledig afhankelijk van de zorgvraag van uw kind. 
              We stemmen de activiteiten, het ritme en de begeleiding af op de individuele 
              behoeften, interesses en mogelijkheden. Zo krijgt elk kind precies de 
              ondersteuning die past.
            </p>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-[#1A4D4D] mb-4">Praktische Informatie</h3>
              <div className="space-y-3 text-gray-600">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-[#FF6347] mt-1 flex-shrink-0" />
                  <p><strong>Openingstijden:</strong> 08:00 - 20:00 uur</p>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#FF6347] mt-1 flex-shrink-0" />
                  <p><strong>Flexibiliteit:</strong> In overleg kunnen tijden worden aangepast</p>
                </div>
                <div className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-[#FF6347] mt-1 flex-shrink-0" />
                  <p><strong>Begeleiding:</strong> 1-op-1 intensieve persoonlijke begeleiding</p>
                </div>
                <div className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-[#FF6347] mt-1 flex-shrink-0" />
                  <p><strong>Begeleiding/dagbesteding:</strong> In klein groepsverband (maximaal 3 kinderen)</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-[#1A4D4D] mb-4">Financiering</h3>
              <p className="text-gray-600 mb-4">
                Onze dagopvang is toegankelijk via:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#FF6347] rounded-full"></span>
                  WLZ (Wet langdurige zorg)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#FF6347] rounded-full"></span>
                  WMO
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#FF6347] rounded-full"></span>
                  Jeugdwet
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <a href="mailto:info@kscare.nl?subject=Meer%20informatie%20dagopvang">
              <Button className="bg-[#FF6347] hover:bg-[#FF5533] text-white rounded-full px-8 py-6 text-lg">
                Neem contact op voor meer informatie
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Dagopvang;